/**
 * Long-form content for individual report pages.
 *
 * Reports listed here render as native /reports/$slug pages.
 * Reports without a details entry continue to link to the legacy
 * perceptica.com URL from the index page.
 */
import { REPORTS } from "./reports";
import aiInPublicDiscourseImage from "@/assets/reports/ai-in-public-discourse.webp";
import bulgarianBankingEuroTransitionImage from "@/assets/reports/bulgarian-banking-euro-transition.webp";
import renewableEnergySeeImage from "@/assets/reports/renewable-energy-see.webp";
import mostMediaVisibleFoundersVenturesImage from "@/assets/reports/most-media-visible-founders-ventures.webp";
import boycottRetailChainsBulgariaImage from "@/assets/reports/boycott-retail-chains-bulgaria.webp";
import pharmaSupplementsVol2Image from "@/assets/reports/pharma-supplements-vol-2.webp";
import pharmaPharmaceuticalsVol1Image from "@/assets/reports/pharma-pharmaceuticals-vol-1.webp";
import supermarketIndustryBulgariaImage from "@/assets/reports/supermarket-industry-bulgaria.webp";
import bankingSectorBulgaria2023Image from "@/assets/reports/banking-sector-bulgaria-2023.webp";
import bankingCommsChallengesImage from "@/assets/reports/banking-comms-challenges.webp";
import retailCommsChallengesImage from "@/assets/reports/retail-comms-challenges.webp";
import pharmaCommsChallengesImage from "@/assets/reports/pharma-comms-challenges.webp";
import bulgarianBankingIndustry2023Image from "@/assets/reports/bulgarian-banking-industry-2023.webp";
import mediaSoutheastEuropeDefenceImage from "@/assets/reports/media-in-southeast-europe-defence-security.webp";
import fromEnergyShockToEnergyTransitionImage from "@/assets/reports/from-energy-shock-to-energy-transition.webp";
import backToSchool2026Image from "@/assets/reports/back-to-school-2026.webp";
import seeNextDigitalisationCover from "@/assets/reports/seenext-digitalisation-cover.webp.asset.json";
import industrySpotlightPharmaCover from "@/assets/reports/industry-spotlight-pharma-in-bulgaria.webp.asset.json";
import eHealthCover from "@/assets/reports/e-health-in-bulgaria.webp.asset.json";
import industrySpotlightBankingCover from "@/assets/reports/industry-spotlight-banking-in-bulgaria.webp.asset.json";

import type { ReportExtendedFields } from "@/content/report-extended";

export type ReportDetails = ReportExtendedFields & {
  /** Public, citable key findings (only facts already stated on the page). */
  keyFindings?: string[];
  /** Public methodology paragraph (sources, period, volume). */
  methodology?: string;
  /** Optional chart image for the key findings section. */
  chartImage?: { src: string; alt: string };
  /** Optional extra sections shown before the takeaways. */
  sections?: { heading: string; paragraphs: string[] }[];
  /** Optional heading replacing the default takeaways lead-in. */
  takeawaysHeading?: string;
  /** Optional extra sections shown after the takeaways (EN only), with an optional external link. */
  sectionsAfterTakeaways?: { heading: string; paragraphs: string[]; link?: { label: string; href: string }; image?: { src: string; alt: string; href?: string }; after?: string[] }[];
  /** Optional library card excerpt (defaults to the first intro paragraph). */
  excerpt?: string;
  /** Optional "who this report is for" paragraph. */
  audience?: { heading: string; text: string };
  /** Optional related internal links (unprefixed paths; locale is applied automatically). */
  related?: { heading: string; links: { label: string; href: string }[] };
  /** Short subhead shown under the H1 (e.g., a guiding question). */
  subtitle: string;
  /** Lead paragraphs that introduce the report. */
  intro: string[];
  /** Bulleted takeaways. The leading bold phrase is rendered as <strong>. */
  takeaways: { lead: string; rest: string }[];
  /** Hero image shown beneath the takeaways. */
  heroImage: { src: string; alt: string };
  /** Optional closing paragraph (no generic boilerplate). */
  closing?: string;
  /** Optional CTA link rendered after the closing paragraph. */
  closingLink?: { label: string; href: string };
  /**
   * Form prompt rendered above the lead-capture form.
   * Defaults to "Want the full insights? ..." if omitted.
   */
  formPrompt?: string;
};

export const REPORT_DETAILS: Record<string, ReportDetails> = {
  "industry-spotlight-banking-in-bulgaria": {
    headline: "Industry Spotlight: Banking in Bulgaria",
    metaDescription: "How Bulgarian consumers talk about property, savings and banks, and where financial scams are spreading in 2026. A report for banking teams.",
    publishedDate: "2026-11-04",
    excerpt: "Bulgarian households are cautious in 2026. Online conversations keep returning to property prices, the cost of living and what to do with savings.",
    subtitle: "How consumers talk about property, savings and banks, and where financial scams are spreading in 2026",
    intro: [
      "Bulgarian households are cautious in 2026. Consumer confidence is under pressure, and online conversations keep returning to the same three worries: property prices, the cost of living and what to do with savings. This Perceptica report combines media monitoring with official data to show how these concerns connect, and what they mean for banks.",
      "Trust runs through all of it. Mortgage terms are described online as complicated and non-transparent, savers are looking beyond deposits, and scams exploit the gap, often with deepfake endorsements from well-known people.",
      "For banks, these are early signals worth tracking. They shape how customers judge your products, your communication and your competitors.",
    ],
    insideItems: [
      "The three consumer concerns that dominate the conversation, and how they connect",
      "How housing prices moved across six major cities in the first half of 2026",
      "Why savers are looking beyond deposits, and what holds them back",
      "Which social platforms carry the highest risk of financial scams",
    ],
    takeaways: [],
    formPrompt: "Fill in the form below and we will email you the report.",
    heroImage: { src: industrySpotlightBankingCover.url, alt: "Industry Spotlight: Banking in Bulgaria, Perceptica report cover" },
  },
  "industry-spotlight-pharma-in-bulgaria": {
    headline: "Industry Spotlight: Pharma in Bulgaria",
    metaDescription: "How Bulgarian media and parents covered the 2026 childhood flu programme, plus vaccine trust and health misinformation risks. A report for pharma teams.",
    publishedDate: "2026-10-26",
    excerpt: "Bulgaria's free childhood flu programme launched in October 2026. How media and parents covered it, and what it reveals about trust in vaccines.",
    subtitle: "What media, parents and platforms say about medicines and vaccines in 2026",
    intro: [
      "Bulgaria's free childhood flu programme launched on 1 October 2026, and supply quickly became part of the story. This Perceptica report looks at how the media covered it, what parents discussed online, and what the wider picture of vaccine trust and health misinformation means for pharma teams.",
    ],
    sections: [
      { heading: "Why this topic matters", paragraphs: ["When official information is thin, people fill the gaps themselves. Teams that spot these signals early can respond with clear information before speculation takes hold."] },
    ],
    insideItems: [
      "How Bulgarian media covered the childhood flu programme, and what went unexplained",
      "What parents discussed on forums, and what stood in their way",
      "How trust in vaccines has shifted over the last decade",
      "Which platforms carry the highest risk of health misinformation",
    ],
    takeaways: [],
    audience: { heading: "Who this report is for", text: "Market access, medical affairs, regulatory, pharmacovigilance and communications teams in pharma." },
    formPrompt: "Fill in the form below and we will email you the report.",
    related: {
      heading: "Related reports",
      links: [
        { label: "Competitive Analysis of The Pharma Industry 2023: Pharmaceuticals (Vol. 1)", href: "/reports/competitive-analysis-pharma-industry-2023-pharmaceuticals" },
        { label: "Competitive Analysis of The Pharma Industry 2023: Supplements (Vol. 2)", href: "/reports/competitive-analysis-of-the-pharma-industry-2023-supplements-vol-2" },
        { label: "E-Health: Age and Regional Patterns in Bulgaria", href: "/reports/e-health-age-and-regional-patterns-in-bulgaria" },
      ],
    },
    heroImage: { src: industrySpotlightPharmaCover.url, alt: "Industry Spotlight: Pharma in Bulgaria, Perceptica report cover" },
  },
  "back-to-school-2026": {
    headline: "Upper-end back-to-school costs in Bulgaria rose about 87% since 2021/22",
    heroStats: [
      { value: "+87%", label: "rise in upper-end school costs since 2021/22" },
      { value: "€600", label: "upper-end spend per child in 2026/27, up from about €320" },
      { value: "€153", label: "state support for eligible pupils, largely unchanged since 2020/21" },
    ],
    chart: {
      type: "bar",
      title: "Upper-end spend on school essentials and uniforms per child",
      unit: "€",
      data: [
        { label: "2021/22", value: 320 },
        { label: "2026/27", value: 600 },
      ],
      source: "Bulgarian traditional media coverage, 2021/22 to 2026/27 (upper-end costs as reported in media)",
    },
    metaDescription: "Upper-end school essentials and uniforms in Bulgaria rose about 87% from €320 to €600 between 2021/22 and 2026/27. Media coverage, themes and sentiment.",
    keyFindings: ["Upper-end spend on school essentials and uniforms: about €600 per child in 2026/27, up from about €320 in 2021/22 (~87%).", "State support for eligible pupils: €153, largely unchanged since it doubled in 2020/21, and paid in two instalments.", "Families cope through borrowing, instalments, buying in stages across terms and second-hand uniforms."],
    methodology: "Bulgarian traditional media coverage of back-to-school expenses for every school year from 2021/22 to 2026/27. The figures are upper-end costs as reported in Bulgarian media, not a retail price survey.",
    sections: [
      { heading: "Why this topic matters", paragraphs: ["Because the back-to-school story returns every year, it offers a consistent way to see how cost-of-living pressure on families is framed in public debate over time."] },
      { heading: "What we analysed", paragraphs: ["Bulgarian traditional media coverage of back-to-school expenses for every school year from 2021/22 to 2026/27. The figures cited are upper-end costs as reported in Bulgarian media, not a retail price survey."] },
    ],
    takeawaysHeading: "Key findings",
    audience: { heading: "Who this report is for", text: "Retail, brand and communications teams who need to see how cost-of-living stories land in public debate, early enough to respond with clarity." },
    subtitle: "Media coverage, key themes and consumer sentiment",
    intro: [
      "Going back to school is one of the most predictable seasonal stories in Bulgarian media - and one of the most expensive for families. For the 2026/27 school year, traditional media put upper-end spend on school essentials and uniforms at around €600 per child, up from about €320 in 2021/22 - a rise of roughly 87%.",
      "Perceptica's media analysis report tracks how that cost story is told: what has become more expensive, how state support compares, and what parents do when the budget falls short.",
    ],
    takeaways: [
      {
        lead: "Costs keep climbing",
        rest: " - upper-end spend on school essentials and uniforms moved from about €320 in 2021/22 to about €600 in 2026/27 (~87%). Stationery, bags, clothing and uniforms all show clear price pressure in media-reported figures.",
      },
      {
        lead: "State support has not kept pace",
        rest: " - support for eligible pupils remains at €153, largely unchanged since it doubled in 2020/21, and is paid in two instalments while most spending happens before the school year starts.",
      },
      {
        lead: "Families stretch the budget",
        rest: " - when costs exceed what households can pay upfront, coverage highlights borrowing, instalments, buying in stages across terms, and second-hand uniforms.",
      },
    ],
    heroImage: {
      src: backToSchool2026Image,
      alt: "Back to School 2026 - Perceptica media analysis report cover showing an 87% rise in school essentials and uniform costs in Bulgaria",
    },
  },
  "e-health-age-and-regional-patterns-in-bulgaria": {
    // TODO(DES-1018): no HubSpot form or PDF yet. The form currently posts to
    // /api/public/report-request (sales notification + backup). Once the E-Health
    // PDF and its HubSpot form exist, add the form ID to HUBSPOT_FORMS in
    // src/components/site/ReportPage.tsx so the PDF is emailed automatically.
    excerpt: "Bulgaria's eHealth platform has 462,679 registered users as of 9 September 2026 - but estimated penetration peaks at 12.9% among 31-50-year-olds and falls to 2.4% among people aged 70+. Age, skills and region still shape who gets in.",
    subtitle: "How age and place shape access to digital healthcare",
    intro: [
      "Bulgaria's national eHealth platform is live - electronic health records, prescriptions, referrals and other medical documentation online. Availability, though, is not the same as equal access. As of 9 September 2026 there are 462,679 registered users. Estimated penetration reaches 12.9% among 31-50-year-olds - the highest of any age group - and falls to just 2.4% among people aged 70+.",
      "This Perceptica report looks at how age and region shape eHealth adoption in Bulgaria, and how those patterns line up with digital skills, internet use and demographic conditions - so policy and industry teams can see where digital healthcare still leaves people behind.",
    ],
    sections: [
      { heading: "Why this topic matters", paragraphs: ["Digital healthcare only works if people can use it. When older citizens and some regions stay offline, eHealth risks creating new forms of exclusion - precisely where dependence on healthcare is often highest - and weakens the transparency benefits the platform is meant to deliver."] },
      { heading: "What we analysed", paragraphs: ["National eHealth registration data as of 9 September 2026, read against National Statistical Institute (NSI) indicators on digital skills, internet use and regional demographics. Penetration figures are registrations as a share of each age group's population; they do not measure active use."] },
    ],
    takeawaysHeading: "Key findings",
    takeaways: [
      { lead: "Adoption peaks in mid-life, not among the oldest", rest: " - Estimated eHealth penetration is about 12.9% among 31-50-year-olds, versus only 2.4% among people aged 70+. Raw registration totals alone understate how steep the age gap is once population size is accounted for." },
      { lead: "Skills and internet use fall sharply with age", rest: " - Only 9.5% of 65-74-year-olds have at least basic digital skills. Among people aged 75-89, 59.7% have never used the internet - consistent with the very low eHealth penetration in the 70+ group." },
      { lead: "Place matters too", rest: " - There is roughly a fourfold gap in internet non-use between the Southwestern region (3.3%) and the North Central region (12.3%), pointing to regional as well as age-based barriers to digital healthcare access." },
    ],
    partnerStudy: {
      name: "Digitalisation in Southeast Europe | 2026 edition",
      url: "https://seenext.org/reports/digitalisation-in-southeast-europe-2026-edition/",
      coverImage: seeNextDigitalisationCover.url,
    },
    audience: { heading: "Who this report is for", text: "Health policy, pharma, public-sector and communications teams who need a clear, evidence-based view of who is - and is not - on eHealth in Bulgaria, and what age and regional gaps mean for access, transparency and inclusion." },
    heroImage: { src: eHealthCover.url, alt: "E-Health: Age and Regional Patterns in Bulgaria - Perceptica report cover" },
  },
  "from-energy-shock-to-energy-transition": {
    keyFindings: ["7,559 media items monitored across Central Europe, the Baltics and Southeast Europe.", "Coverage peaked five times in three months as the Strait of Hormuz disruption unfolded.", "In the second half of the period, renewables, storage and electrification appeared alongside fuel prices.", "Solar and battery storage were the clean technologies mentioned most often."],
    methodology: "Media coverage in Central Europe, the Baltics and Southeast Europe from March to May 2026, covering 7,559 media items on Europe's energy security and the disruption to the Strait of Hormuz.",
    subtitle: "How European media turned a Middle East energy crisis into a transition story",
    intro: [
      "Between March and May 2026, developments in the Middle East and the disruption to the Strait of Hormuz put Europe's energy security back at the centre of public debate. Perceptica monitored 7,559 media items across Central Europe, the Baltics and Southeast Europe to see how that story was told.",
      "The report follows the coverage from the first signs of tension through five distinct peaks, tracking how the narrative moved from immediate supply risk to a wider debate about fuel prices, inflation and Europe's long-term energy resilience.",
    ],
    takeaways: [
      { lead: "A crisis that kept escalating", rest: " - coverage peaked five times over three months as the situation in the Strait of Hormuz moved from a geopolitical risk to a structural test of Europe's energy system." },
      { lead: "The story didn't stay about oil and gas", rest: " - by the second half of the monitoring period, renewables, storage and electrification appeared alongside fuel prices as part of the same conversation." },
      { lead: "Solar and battery storage led the transition narrative", rest: " - among specific clean technologies, these two came up most often as the practical, near-term response to fossil-fuel exposure." },
    ],
    heroImage: {
      src: fromEnergyShockToEnergyTransitionImage,
      alt: "From Energy Shock to Energy Transition - Perceptica report cover",
    },
  },
  "media-in-southeast-europe-common-narratives-in-nato-members": {
    keyFindings: ["4,221 publications monitored in Bulgaria, Greece, Romania and Serbia.", "Geopolitical tensions made up 63% of defence coverage in Greece and 61% in Romania.", "Three dominant themes: geopolitical tensions and regional instability, international cooperation and joint defence initiatives, and defence modernisation and procurement.", "Serbian media built a distinct narrative around military readiness and an 'island of stability' framing."],
    methodology: "Defence and security coverage in Bulgarian, Greek, Romanian and Serbian media from January to March 2026 (Q1), covering 4,221 publications. Part of the Defence & Security in Southeast Europe - 2026 Edition published by SeeNext.",
    subtitle: "How Bulgaria, Greece, Romania and Serbia covered defence and security in Q1 2026",
    intro: [
      "How did Bulgarian, Greek, Romanian, and Serbian media cover defence and security in the first quarter of 2026? The patterns reveal more than shared concerns - they reveal a region navigating geopolitical pressure in very different ways.",
      "Perceptica monitored 4,221 publications across four countries from January to March 2026. Three dominant themes shaped regional coverage: geopolitical tensions and regional instability, international cooperation and joint defence initiatives, and defence modernisation and procurement. While Bulgaria, Greece, and Romania converged on a broadly unified narrative, Serbia's media told a strikingly different story.",
    ],
    takeaways: [
      {
        lead: "Geopolitical tensions dominate coverage in NATO members",
        rest: " - 63% of defence coverage in Greece and 61% in Romania, while Bulgaria tells a more fragmented story.",
      },
      {
        lead: "Serbia constructs a distinct narrative",
        rest: " around military readiness and an 'island of stability' framing that sets it apart from its NATO neighbours.",
      },
      {
        lead: "Defence procurement diverges country by country",
        rest: " - from F-16 delays to Chinese supersonic missiles, the data shows how each country frames modernisation and arms deals.",
      },
      {
        lead: "NATO members vs a non-member state",
        rest: " - the report reveals how the same regional pressures are processed in fundamentally different ways across the four media landscapes.",
      },
    ],
    heroImage: {
      src: mediaSoutheastEuropeDefenceImage,
      alt: "Media in Southeast Europe: common narratives in NATO members, Serbia stands out - Perceptica report cover",
    },
    partnerStudy: {
      name: "Defence & Security in Southeast Europe - 2026 Edition",
      url: "https://seenext.org/reports/defence-security-in-southeast-europe-2026-edition/",
    },
  },
  "ai-in-public-discourse": {
    keyFindings: ["Work and business is the leading context of use, with 31% of all AI discussions.", "Creativity and personal use are tied at 26% each.", "Overall sentiment is nearly balanced but slightly negative.", "Top concerns: job displacement, fraud and child safety."],
    methodology: "Public conversation about artificial intelligence on Bulgarian social media, news outlets and online forums from July to December 2025.",
    subtitle: "How Bulgarians feel about artificial intelligence?",
    intro: [
      "Between July and December 2025, Artificial Intelligence dominated public conversation across Bulgarian social media, news outlets, and online forums. What began as a wave of curiosity and enthusiasm quickly evolved into a more complex and divided debate - one shaped equally by practical adoption and growing societal concern.",
      "Perceptica's latest media analysis examines public sentiment towards AI in Bulgaria during this period, tracking how different audiences perceive its value, where fears are concentrated, and which narratives are gaining momentum. From job displacement anxiety and fraud concerns to enthusiasm around productivity and creative tools, the report captures the full spectrum of public opinion as AI moves from novelty to everyday reality.",
    ],
    takeaways: [
      {
        lead: "Sentiment is nearly balanced but slightly negative",
        rest: ", reflecting a public that sees real value in AI but remains cautious about its broader societal impact.",
      },
      {
        lead: "Work and business is the dominant context of use",
        rest: ", accounting for 31% of all AI discussions, where people talk about productivity gains, research support and workflow optimisation.",
      },
      {
        lead: "Creativity and personal use are tied at 26% each",
        rest: ", highlighting AI's strong presence in content creation, problem-solving and everyday entertainment.",
      },
      {
        lead: "Job displacement, fraud and child safety are the top concerns",
        rest: ", positioning AI as a technology that could amplify existing vulnerabilities if left unregulated.",
      },
    ],
    heroImage: {
      src: aiInPublicDiscourseImage,
      alt: "AI in public discourse - Perceptica report cover",
    },
  },
  "bulgarian-banking-and-the-euro-transition": {
    keyFindings: ["Sentiment towards banks: 30.4% positive, 29.8% negative, 39.8% neutral.", "Facebook drove 59% of all conversation.", "Interest rate concerns generated 1,884 publications, ahead of dual labelling with 1,448."],
    methodology: "Public sentiment towards Bulgarian banks across media channels and social platforms during euro adoption, from October 2025 to January 2026.",
    subtitle: "How public perception could make or break Bulgaria's historic currency switch",
    intro: [
      "Between October 2025 and January 2026, Bulgaria's euro adoption dominated public discourse across media channels, social platforms, and everyday conversations. What started as a technical banking transition quickly evolved into a broader test of institutional trust, social fairness, and governance credibility.",
      "Perceptica's latest media analysis examines public sentiment towards Bulgarian banks during this critical period, monitoring how expectations were set, where concerns emerged, and which narratives gained traction. From planned system updates that sparked anxiety to dual pricing confusion and interest rate fears, the report captures the full spectrum of public reaction as Bulgaria navigates its most significant monetary change in decades.",
    ],
    takeaways: [
      {
        lead: "Public sentiment is polarized but balanced",
        rest: " - 30.4% positive, 29.8% negative, 39.8% neutral - meaning no dominant narrative has formed yet, making strategic communication critical.",
      },
      {
        lead: "Facebook drives 59% of all conversation",
        rest: ", acting as the primary amplification platform where banking updates quickly turn into public debate.",
      },
      {
        lead: "Interest rate concerns generated 1,884 publications",
        rest: ", making it the top fear, far more than dual labelling (1,448 publications) or operational disruptions.",
      },
    ],
    heroImage: {
      src: bulgarianBankingEuroTransitionImage,
      alt: "Bulgarian banking and the euro transition - Perceptica report cover",
    },
  },
  "renewable-energy-in-the-southeast-europe-region": {
    subtitle: "How the media shapes the green transition in the Balkans",
    intro: [
      "Between January and March 2025, renewable energy was a prominent topic in Bulgarian media, with significant focus on developments across Southeast Europe. Media coverage spanned investment potential, energy security, infrastructure gaps, and regulatory challenges. Bulgaria led in visibility, followed by Romania and Greece - three countries navigating complex transitions with EU targets in sight.",
      "Perceptica's latest media analysis explores how renewable energy is portrayed in top-tier Bulgarian media, with a regional lens on the rest of the Balkans. From solar advancements and cross-border collaborations to public resistance and policy delays, the report captures how narratives differ across countries and what they mean for governments, investors, and energy stakeholders.",
    ],
    takeaways: [
      {
        lead: "Bulgaria has the highest media visibility",
        rest: ", but faces mounting pressure from the EU to accelerate permitting and implementation processes for renewable projects.",
      },
      {
        lead: "Positive narratives centre around solar, hydro, and cross-border projects",
        rest: ", while negative coverage is often linked to local opposition, policy delays, or concerns about environmental impact.",
      },
      {
        lead: "Media sentiment varies by country",
        rest: ": Romania and Greece show consistent momentum, while markets like Moldova and North Macedonia face structural and political challenges.",
      },
    ],
    heroImage: {
      src: renewableEnergySeeImage,
      alt: "Renewable energy in the Southeast Europe region - Perceptica report cover",
    },
  },
  "the-most-media-visible-founders-ventures-in-bulgaria": {
    subtitle: "How media visibility shapes Bulgaria's startup and innovation ecosystem",
    intro: [
      "Between January and May 2025, Bulgarian media coverage of the startup and tech ecosystem was shaped by several high-impact moments - from space exploration milestones to advancements in AI, fintech, and logistics. The public conversation was led by a handful of high-profile founders, ventures, and investment funds whose activity and innovation captured national attention.",
      "Perceptica's latest media analysis explores how founders and companies were portrayed in top-tier Bulgarian media outlets, including online publications, print press, TV, and social media.",
    ],
    takeaways: [
      {
        lead: "EnduroSat led the narrative",
        rest: ", accounting for over 70% of startup-related media visibility following the historic launch of Bulgaria's first nanosatellite in partnership with SpaceX.",
      },
      {
        lead: "LAUNCHub Ventures dominated investor mentions",
        rest: ", largely driven by coverage of its portfolio companies and visibility at major tech events. Vitosha Venture Partners and Eleven Ventures also stood out for their ecosystem engagement.",
      },
      {
        lead: "Media sentiment was predominantly neutral-to-positive",
        rest: ", with no significant negative coverage detected. Broadcast and social media channels showed the highest share of positive tone.",
      },
    ],
    heroImage: {
      src: mostMediaVisibleFoundersVenturesImage,
      alt: "The most media-visible founders and ventures in Bulgaria - Perceptica report cover",
    },
  },
  "the-boycott-of-retail-chains-in-bulgaria": {
    subtitle: "How a regional consumer protest became a national media story",
    intro: [
      "Between February 1 and 12, a boycott of major retail chains in Bulgaria sparked widespread discussion across media and social platforms in the country. The boycott is part of a broader wave of consumer protests across Southeast Europe. Globally, the campaign initially started in Croatia, where actions are still ongoing, and was soon followed by Serbia, Montenegro, Bosnia and Herzegovina, North Macedonia, and Slovenia.",
      "Perceptica's latest report analyses how the boycott was covered, the main narratives that emerged, and the key players driving the conversation during the stated period. Understanding how media narratives and public sentiment evolve is crucial for businesses, policymakers, and market analysts. Monitoring media coverage helps brands anticipate consumer reactions and adjust their strategies accordingly.",
    ],
    takeaways: [
      {
        lead: "A regional protest with national resonance",
        rest: " - the campaign that began in Croatia rapidly spread across Southeast Europe, reshaping how Bulgarian consumers and media talk about retail prices.",
      },
      {
        lead: "Media narratives evolved quickly across a 12-day window",
        rest: ", moving from coverage of the boycott itself to broader debates on inflation, retail margins and consumer power.",
      },
      {
        lead: "Brands cannot afford to be passive",
        rest: " - tracking how key players, journalists and influencers framed the boycott reveals where reputational risk concentrates and how to respond before sentiment hardens.",
      },
    ],
    heroImage: {
      src: boycottRetailChainsBulgariaImage,
      alt: "Public sentiment around the boycott of retail chains in Bulgaria - Perceptica report cover",
    },
  },
  "competitive-analysis-of-the-pharma-industry-2023-supplements-vol-2": {
    sections: [
      { heading: "Why this topic matters", paragraphs: ["Supplement and health-product brands compete in a crowded market where media presence builds trust. Knowing which producers lead the conversation - and how much smaller it is than the pharmaceuticals conversation - helps teams set realistic share-of-voice goals."] },
      { heading: "What we analysed", paragraphs: ["The second part of our media analysis of the pharmaceutical industry in Bulgaria, covering January to July 2023, focused on producers of pharmaceuticals and health supplements."] },
    ],
    takeawaysHeading: "Key findings",
    audience: { heading: "Who this report is for", text: "Supplement and health-product brands, and communications teams planning PR in the wider pharma category." },
    subtitle: "Who leads the conversation among Bulgarian supplement and health-product brands",
    intro: [
      "The second part of our thorough media analysis on publications related to the pharmaceutical industry in Bulgaria, covering the period January - July 2023.",
      "This time we focus on the producers of pharmaceuticals and health supplements. Our study suggests that the top-performing brands in this domain are Naturpharma, ABO Pharma, and Queisser Pharma. The media mentions of these three brands collectively account for 51% of the total mentions for pharmaceutical and health supplement producers.",
      "We also observe a significant reduction in media interest compared to the one received by the companies in our previous report on pharmaceutical producers. For deeper insights such as the dominant media channel within the industry or the top-performing products for each brand, download the report.",
    ],
    takeaways: [
      {
        lead: "Three brands command 51% of the conversation",
        rest: " - Naturpharma, ABO Pharma and Queisser Pharma together account for the majority of media mentions among Bulgarian supplement and health-product producers.",
      },
      {
        lead: "Supplement producers attract less media interest than pharmaceutical producers",
        rest: ", a notable drop versus our Vol. 1 report - useful context for share-of-voice benchmarking and PR planning.",
      },
      {
        lead: "Channel mix and product-level performance matter",
        rest: " - the full report breaks down dominant media channels and the top-performing products per brand, so teams can target the right outlets and SKUs.",
      },
    ],
    heroImage: {
      src: pharmaSupplementsVol2Image,
      alt: "Competitive analysis of the pharma industry 2023: supplements (Vol. 2) - Perceptica report cover",
    },
  },
  "competitive-analysis-pharma-industry-2023-pharmaceuticals": {
    sections: [
      { heading: "Why this topic matters", paragraphs: ["Pharmaceuticals is one of the most trust-sensitive industries in Bulgaria, and the media conversation around producers shapes how patients, pharmacists and healthcare professionals perceive each brand. Knowing who leads that conversation is the starting point for competitive PR and positioning."] },
      { heading: "What we analysed", paragraphs: ["Media coverage of the leading pharmaceutical producers in Bulgaria from January to July 2023 - a competitive analysis of the coverage the main players received, collected with Perceptica's media listening platform and analysed by our expert team."] },
    ],
    takeawaysHeading: "Key findings",
    audience: { heading: "Who this report is for", text: "Pharma brand, communications and marketing teams benchmarking share of voice and planning PR or competitive positioning." },
    subtitle: "Who leads the conversation among Bulgarian pharmaceutical producers",
    intro: [
      "Our expert team conducted a thorough analysis of media coverage of the leading companies in Bulgaria's pharmaceutical industry. This first volume focuses on producers of pharmaceuticals, while a separate report covers producers of health supplements.",
      "The report covers the period January - July 2023 and provides a competitive analysis of the media coverage that the main players received. The leaders Bayer Bulgaria, Sanofi and Sopharma collectively account for 64% of the overall conversation on the topic.",
    ],
    takeaways: [
      {
        lead: "Three brands command 64% of the conversation",
        rest: " - Bayer Bulgaria, Sanofi and Sopharma dominate share of voice among pharmaceutical producers in Bulgarian media.",
      },
      {
        lead: "Pharmaceutical producers attract notably more media interest than supplement producers",
        rest: ", a useful benchmark when planning PR investment and competitive positioning across the wider pharma category.",
      },
      {
        lead: "Channel mix and source-level performance matter",
        rest: " - the full report uncovers the dominant media channel within the industry and the sources publishing the highest volume of content, so teams can target the right outlets.",
      },
    ],
    heroImage: {
      src: pharmaPharmaceuticalsVol1Image,
      alt: "Competitive analysis of the pharma industry 2023: pharmaceuticals (Vol. 1) - Perceptica report cover",
    },
  },
  "analysis-supermarket-industry-in-bulgaria": {
    sections: [
      { heading: "Why this topic matters", paragraphs: ["Grocery retail is one of the most competitive consumer categories in Bulgaria, and the media conversation around it shapes how shoppers perceive value, quality and trust. Knowing which chains set that narrative - and which are absent from it - is the starting point for any competitive communications plan in the sector."] },
      { heading: "What we analysed", paragraphs: ["Media coverage of the supermarket industry in Bulgaria from January to June 2023, mapping how the conversation splits across the leading grocery chains, which channels carry it and which sources publish the highest volume of content."] },
    ],
    takeawaysHeading: "Key findings",
    audience: { heading: "Who this report is for", text: "Retail, brand and communications teams who need a clear read on share of voice in Bulgarian grocery retail - before planning competitive PR or category-level positioning." },
    subtitle: "Who dominates Bulgaria's grocery retail conversation in the media",
    intro: [
      "This report focuses on the media coverage of the supermarket industry in Bulgaria during the first half of 2023. The competitive landscape of grocery chains is led by Lidl, Kaufland, Billa and Fantastico, which together account for 90% of the entire conversation on the topic.",
      "Hypermarket chains such as T Market, Metro and Promarket are notably less visible in the media, leaving a clear gap between the top tier and the challengers - useful context for any brand planning competitive PR or category-level positioning.",
    ],
    takeaways: [
      {
        lead: "Four chains dominate 90% of the conversation",
        rest: " - Lidl, Kaufland, Billa and Fantastico set the narrative for Bulgarian grocery retail in the media.",
      },
      {
        lead: "Hypermarket chains are far less visible",
        rest: " - T Market, Metro, Promarket and others trail the leaders by a wide margin, signalling an opportunity gap for challenger brands.",
      },
      {
        lead: "Channel mix and source-level performance matter",
        rest: " - the full report breaks down the dominant media channels and the sources publishing the highest volume of content, so teams can target the right outlets.",
      },
    ],
    heroImage: {
      src: supermarketIndustryBulgariaImage,
      alt: "Competitive analysis of the supermarket industry in Bulgaria - Perceptica report cover",
    },
  },
  "media-intelligence-banking-sector-bulgaria": {
    sections: [
      { heading: "Why this topic matters", paragraphs: ["Banking is a trust business, and the media conversation around the sector shapes how clients, regulators and the wider public perceive stability and credibility. Knowing which stories dominate, when attention peaks and which channels carry the discussion is the starting point for any banking communications plan."] },
      { heading: "What we analysed", paragraphs: ["Media coverage of the Bulgarian banking sector during the first four months of 2023 - the top stories that dominated public discussion, content volume over time, the volume peaks that defined the news cycle and the most relevant media channels in the field."] },
    ],
    takeawaysHeading: "Key findings",
    audience: { heading: "Who this report is for", text: "Banking communications, PR and strategy teams who need a clear read on the sector's media landscape before planning campaigns, benchmarking competitors or prioritising outreach." },
    subtitle: "Top stories, volume peaks and channel trends shaping Bulgarian banking media in early 2023",
    intro: [
      "Gain a clear view of the banking sector in Bulgaria during the first four months of 2023 with our comprehensive media monitoring report. It surfaces the industry's trends and pinpoints the most relevant media channels in the field.",
      "The report covers the top stories that dominated public discussion, content volume over time and the volume peaks that defined the news cycle - giving banking communications and strategy teams the context they need to make informed decisions.",
    ],
    takeaways: [
      {
        lead: "The top stories that dominated banking discussion",
        rest: " - identified across the first four months of 2023, so teams can see which narratives carried the most weight.",
      },
      {
        lead: "Content volume over time and key volume peaks",
        rest: " - mapping how attention rose and fell, with the peak moments that defined the news cycle.",
      },
      {
        lead: "The most relevant media channels for the banking sector",
        rest: " - useful for PR planning, competitive benchmarking and prioritising outreach.",
      },
    ],
    heroImage: {
      src: bankingSectorBulgaria2023Image,
      alt: "Media monitoring report on the Bulgarian banking industry - Perceptica report cover",
    },
  },
  "communications-challenges-in-the-banking-industry": {
    subtitle: "What is the role of communications in building trust in banks?",
    intro: [
      "Arguably more so than in other sectors, when trust in financial institutions is jeopardised, the consequences can go far beyond shareholders and clients to affect entire economies. That makes communications a strategic function in banking, not just a support one.",
      "On 31 May, the Comms Talks: Banks event explored the role of communications professionals in building and maintaining trust. Starting from the premise that knowledge breeds trust, a key focus was financial literacy initiatives and the potential for communications to serve as an instrument for financial stability.",
    ],
    takeaways: [
      {
        lead: "Trust in banks is a systemic asset",
        rest: " - when it breaks, the consequences ripple beyond clients and shareholders to affect the wider economy.",
      },
      {
        lead: "Knowledge breeds trust",
        rest: " - financial literacy initiatives are emerging as a core lever for communications teams in banking.",
      },
      {
        lead: "Media monitoring and analysis can directly support better bank performance",
        rest: " - and contribute to overall financial stability when used as part of a structured communications strategy.",
      },
    ],
    heroImage: {
      src: bankingCommsChallengesImage,
      alt: "Communications challenges in the banking industry - Perceptica report cover",
    },
  },
  "communications-challenges-in-the-retail-sector": {
    subtitle: "What drives shopper sentiment online - and where supermarkets should focus their communications",
    intro: [
      "This report analyses online user sentiment - drawn from traditional and online media monitoring - on a few key, sector-specific topics for retail. It looks at how product quality defines users' opinion online, where supermarkets must focus their efforts, and which factor is the biggest driver of the online conversation about the sector.",
      "For communications and marketing teams in retail, the result is a clear read on what actually moves the needle - and where the noise is loudest - so resources can be aimed at the conversations that matter most.",
    ],
    takeaways: [
      {
        lead: "Product quality defines online opinion of supermarkets",
        rest: " - a single quality issue can outweigh strong campaigns and shape sentiment for weeks.",
      },
      {
        lead: "The report pinpoints where supermarkets should focus communications efforts",
        rest: " - so retail teams can stop spreading effort thinly and concentrate on the topics that actually move sentiment.",
      },
      {
        lead: "It identifies the single biggest driver of the online retail conversation",
        rest: " - giving comms teams a clear priority to act on, not just a list of trends.",
      },
    ],
    heroImage: {
      src: retailCommsChallengesImage,
      alt: "Communications challenges in the retail sector - Perceptica report cover",
    },
  },
  "communications-challenges-in-the-pharma-industry": {
    subtitle: "How effective is pharma at getting its messages across to an increasingly (mis-)informed audience?",
    intro: [
      "Comms Talks: Pharma is the first edition of Comms Talks - a series of six sector-specific discussions designed to foster dialogue between in-house teams, agencies, media and media analysis providers. By creating an inclusive, interdisciplinary platform, the series consolidates the industry's silos and offers a critical, multilateral view of the challenges practitioners face. Comms Talks is a collaboration between Perceptica and EACD, with supporting partners BDVO and AMEC.",
      "The post-event report features analysis of the social media conversation on several key pharma topics, including the online conversation about diabetes, the lack of trust in general practitioners, and the narratives around homeopathy and home treatment.",
    ],
    takeaways: [
      {
        lead: "The online conversation about diabetes",
        rest: " - a major recurring topic for pharma comms, mapped through real social-media data so teams know where the conversation actually lives.",
      },
      {
        lead: "The lack of trust in general practitioners",
        rest: " - a structural challenge that shapes how pharma messages are received, interpreted and re-shared online.",
      },
      {
        lead: "Homeopathy and home treatment",
        rest: " - a competing narrative space pharma teams must understand if they want to communicate effectively to an increasingly self-informed audience.",
      },
    ],
    heroImage: {
      src: pharmaCommsChallengesImage,
      alt: "Communications challenges in the pharma industry - Perceptica report cover",
    },
  },
  "bulgarian-banking-industry-report": {
    sections: [
      { heading: "Why this topic matters", paragraphs: ["Mergers and acquisitions reshape a bank's online reputation long after the deal closes. Understanding how the social conversation reacts during deal cycles - and whether a bank's portfolio matches its perception - helps teams protect trust when it matters most."] },
      { heading: "What we analysed", paragraphs: ["Recent mergers and acquisitions in the Bulgarian banking sector and their impact on the online reputation of the participating banks, with a focus on the social media conversation: how each bank's portfolio aligns with its online perception, what drives reputation during deal cycles and where the most consequential conversations take place."] },
    ],
    takeawaysHeading: "Key findings",
    audience: { heading: "Who this report is for", text: "Banking communications, strategy and reputation teams navigating mergers, acquisitions or major portfolio changes - and anyone who needs to know where the post-deal conversation really happens." },
    subtitle: "Mergers and acquisitions with profound effect upon Bulgarian banks' online reputation",
    intro: [
      "Perceptica's report on the Bulgarian banking industry analyses recent M&A activity and its impact on the involved banks' online reputation, with a focus on the social media conversation.",
      "It examines how a bank's portfolio corresponds to its perception online, what actually moves reputation during deal cycles, and where the most consequential conversations are taking place.",
    ],
    takeaways: [
      {
        lead: "How a bank's portfolio corresponds to its social media perception",
        rest: " - mapping product mix and positioning against share of voice and sentiment to show where reputation is earned or lost.",
      },
      {
        lead: "What actually moves banks' online reputation during M&A",
        rest: " - the drivers, narratives and inflection points that shape sentiment before, during and after a deal.",
      },
      {
        lead: "Where the post-deal conversation lives",
        rest: " - the channels, audiences and recurring themes that decide how an acquisition is read by the Bulgarian public online.",
      },
    ],
    heroImage: {
      src: bulgarianBankingIndustry2023Image,
      alt: "Bulgarian banking industry report - Perceptica analysis of M&A and online reputation",
    },
  },
};

// Pre-2026 reports: dated at the end of the period they monitored.
const PERIOD_END_DATES: Record<string, string> = {
  "renewable-energy-in-the-southeast-europe-region": "2025-03-31",
  "the-most-media-visible-founders-ventures-in-bulgaria": "2025-05-31",
  "the-boycott-of-retail-chains-in-bulgaria": "2025-02-12",
  "bulgarian-banking-industry-report": "2023-04-30",
  "analysis-supermarket-industry-in-bulgaria": "2023-06-30",
  "competitive-analysis-pharma-industry-2023-pharmaceuticals": "2023-07-31",
  "competitive-analysis-of-the-pharma-industry-2023-supplements-vol-2": "2023-07-31",
  "communications-challenges-in-the-banking-industry": "2019-05-31",
  "communications-challenges-in-the-pharma-industry": "2019-12-31",
  "communications-challenges-in-the-retail-sector": "2018-12-31",
};

// Fill missing publishedDate from period end, else the stored updatedAt date.
for (const r of REPORTS) {
  const d = REPORT_DETAILS[r.slug];
  if (!d || d.publishedDate) continue;
  const date = PERIOD_END_DATES[r.slug] ?? r.updatedAt;
  if (date) d.publishedDate = date;
}
